import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import useBreakpoint from "../../../../Grid/hooks/useBreakpoint";
import { ILandingInnovationProductUmami } from "../LandingInnovationUmami";
import { useHighlightedTypographyContext } from "../../../../HighlightedTypography";
import Button from "../../../../../Foundation/Button/Button";

import Typography from "../../../../../Foundation/Typography/Typography";
import FlexBox from "../../../../FlexBox/FlexBox";
import ShowMoreLink from "../../../../../Navigation/ShowMoreLink/ShowMoreLink";
import { _InnovationProductUmamiRoot } from "./InnovationProductUmami.styled";
import Divider from "../../../../Divider/Divider";

export interface IInnovationProductUmamiProps<T extends any = any> {
  product: ILandingInnovationProductUmami<T>;
  className?: string;
  onClick?: (product: ILandingInnovationProductUmami) => void;
  showLessLabel: string;
  showMoreLabel: string;
  ctaLabel?: string;
  hideCta?: boolean;
  renderVideo?: (
    videoSlug: string,
    videoRef: React.MutableRefObject<HTMLVideoElement | null>,
    props: React.VideoHTMLAttributes<HTMLVideoElement>
  ) => React.ReactNode;
}

const InnovationProductUmami = <T extends any = any>(
  props: IInnovationProductUmamiProps<T>
) => {
  const {
    product,
    className,
    onClick,
    showLessLabel,
    showMoreLabel,
    ctaLabel: _ctaLabel,
    hideCta: hideCtaProp,
    renderVideo,
  } = props;
  const videoSlug = product.videoSlug;
  const { mobile, isMobileAndLg } = useBreakpoint();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const productRef = useRef<HTMLDivElement>(null);

  const ctaLabel = _ctaLabel || product.ctaLabel || "Je commande";
  const hideCta =
    hideCtaProp === true ||
    Boolean((product as { hideCta?: boolean }).hideCta);
  const { highlightedColor: highlightedColorContext } =
    useHighlightedTypographyContext();

  const [showMore, setShowMore] = useState<boolean>(false);
  const [isClamped, setClamped] = useState(false);

  const handleShowMore = () => {
    setShowMore(!showMore);
  };

  // Auto-play when element is in viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (mobile) {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              videoRef.current?.play();
            } else {
              setIsVisible(false);
              videoRef.current?.pause();
            }
          });
        }
      },
      { threshold: 0.8 }
    );

    if (productRef.current && mobile) {
      observer.observe(productRef.current);
    }

    return () => {
      if (productRef.current) {
        observer.unobserve(productRef.current);
      }
    };
  }, [mobile]);

  // Default video renderer if renderVideo is not provided
  const defaultVideoRenderer = () => {
    if (!videoSlug) return null;

    return (
      <video
        ref={videoRef}
        muted
        loop
        playsInline
        autoPlay={isVisible}
        src={videoSlug}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    );
  };

  return (
    <_InnovationProductUmamiRoot
      direction="column"
      gap={12}
      className={clsx(className, { "action-on-hover": "" })}
      onMouseEnter={() => {
        if (!mobile) {
          setIsVisible(true);
          videoRef.current?.play();
        }
      }}
      onMouseLeave={() => {
        if (!mobile) {
          setIsVisible(false);
          videoRef.current?.pause();
        }
      }}
      ref={productRef}
    >
      <div className="innovation-umami-product-video">
        {renderVideo
          ? renderVideo(videoSlug, videoRef, {
              muted: true,
              loop: true,
              playsInline: true,
              autoPlay: isVisible,
              poster: product.poster,
            })
          : defaultVideoRenderer()}
      </div>
      <FlexBox
        className="innovation-umami-product-content"
        direction="column"
        align="center"
        gap={13}
        style={{
          maxWidth: isMobileAndLg ? "80%" : "60%",
          margin: "0 auto",
          padding: "40px 16px",
          position: "relative",
          zIndex: 2,
        }}
      >
        <FlexBox
          gap={10}
          style={{
            padding: "8px 13px",
            border: "1px solid white",
            visibility: product.tag ? "visible" : "hidden",
            marginBottom: isMobileAndLg ? 16 : 19 + 24,
          }}
        >
          <Typography variant="ui" color="white">
            {product.tag || "_"}
          </Typography>
        </FlexBox>
        <Typography
          variant={"h5"}
          color="white"
          style={{ textAlign: "center" }}
        >
          {product.title}
        </Typography>
        <Typography
          variant="body2"
          color="white"
          ellipse={!showMore}
          ellipseLine={3}
          showSeeMore={true}
          onClamped={setClamped}
          style={{ textAlign: "center" }}
        >
          {product.description}
        </Typography>
        {isClamped && (
          <ShowMoreLink
            // noIcon
            icon={showMore ? "minus" : "plus"}
            label={showMore ? showLessLabel : showMoreLabel}
            color={highlightedColorContext}
            onClick={handleShowMore}
          />
        )}

        <FlexBox style={{ marginTop: "auto" }} wrap={"wrap"} gap={10}>
          <Typography variant={"h6"} useContextColor={true} color="white">
            {product.price}
          </Typography>
          {product.priceSubtitle && (
            <>
              <Divider direction="vertical" weight={2} />
              <Typography variant="h6light" color="white">
                {product.priceSubtitle}
              </Typography>
            </>
          )}
        </FlexBox>

        {!hideCta && (
          <div>
            <Button
              variant="champagne"
              className={clsx("innovation-umami-product-action-button")}
              onClick={() => onClick?.(product)}
            >
              {ctaLabel}
            </Button>
          </div>
        )}
      </FlexBox>
    </_InnovationProductUmamiRoot>
  );
};

export default InnovationProductUmami;
